import clsx from 'clsx';
import React, { useCallback } from 'react'
import { Offcanvas } from 'react-bootstrap'
import { useForm, Controller } from 'react-hook-form';
import { CreateInventoryFormData, CreateInventorySchema } from '../../../services/inventory/schema/CreateInventorySchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCreateInventory } from '../../../services/inventory/hooks/useCreateInventory';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../../auth/context/AuthProvider';
import useIndexInventoryCategory from '../../../services/inventory-category/hooks/useIndexInventoryCategory';
import CurrencyInputRef from '../../../shared/components/form/CurrencyInputRef';

interface CreateInventoryDialogProps {
    show: boolean;
    onClose: () => void;
}

const CreateInventoryDialog: React.FC<CreateInventoryDialogProps> = ({ show, onClose }) => {
    const { user } = useAuth();

    const { register, handleSubmit, formState: { errors }, reset, setError, control } = useForm<CreateInventoryFormData>({
        resolver: zodResolver(CreateInventorySchema),
        defaultValues: {
            property_id: user?.active_property_id ?? '',
            category_id: '',
            item_name: '',
            price: 0,
        }
    });

    // Fetch inventory categories for dropdown
    const { data: categoriesData, isSuccess: categoriesLoaded } = useIndexInventoryCategory({
        page: 1,
        limit: 100,
    });

    const handleClose = useCallback(() => {
        reset();
        onClose();
    }, [reset, onClose]);

    const { mutate, isPending } = useCreateInventory();

    const queryClient = useQueryClient();

    const onSubmit = useCallback((data: CreateInventoryFormData) => {
        if (isPending) return;

        // Ensure property_id is set from auth
        const payload = {
            ...data,
            property_id: user?.active_property_id ?? data.property_id,
        };

        toast.loading('Creating inventory...', { id: 'create-inventory' });
        mutate(payload, {
            onSuccess: async () => {
                handleClose();

                await queryClient.refetchQueries({
                    predicate: (query) => query.queryKey.some((key) => ['inventories'].includes(key as string))
                });

                toast.success('Inventory created successfully', { id: 'create-inventory' });
            },
            onError: (mutationError) => {
                if ((mutationError as any)?.status === 422) {
                    const validationErrors = (mutationError as any)?.response?.data?.errors;
                    if (validationErrors) {
                        for (const [field, messages] of Object.entries(validationErrors)) {
                            setError(field as keyof CreateInventoryFormData, {
                                type: "manual",
                                message: (messages as string[])[0],
                            });
                        }
                        toast.error('Failed to create inventory', { id: 'create-inventory' });
                    }
                } else {
                    toast.error(mutationError?.message || 'Failed to create inventory', { id: 'create-inventory' });
                }
            }
        });
    }, [mutate, user, isPending, handleClose, queryClient, setError]);

    return (
        <Offcanvas show={show} onHide={handleClose} placement="end" className="offcanvas-responsive">
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>Create Inventory</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                <form onSubmit={handleSubmit(onSubmit)} className="d-flex flex-column" style={{ height: '100%' }}>
                    <div className="action-sheet-content flex-grow-1">
                        {/* Hidden property_id field */}
                        <input type="hidden" {...register('property_id')} />

                        {/* Category */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="category_id" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Category: </label>
                            <div className="col-sm-8">
                                <select
                                    className={clsx("form-control app-form-control rounded-1", errors?.category_id && 'is-invalid')}
                                    id="category_id"
                                    {...register('category_id')}
                                    style={{ fontSize: '14px', height: '35px' }}
                                >
                                    <option value="">Select Category</option>
                                    {categoriesLoaded && categoriesData?.data.map((category) => (
                                        <option key={category.id} value={category.id}>{category.name}</option>
                                    ))}
                                </select>
                                {errors?.category_id && <div className="invalid-feedback">{errors.category_id.message}</div>}
                            </div>
                        </div>

                        {/* Item Name */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="item_name" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Item Name: </label>
                            <div className="col-sm-8">
                                <input
                                    type="text"
                                    className={clsx("form-control app-form-control rounded-1", errors?.item_name && 'is-invalid')}
                                    id="item_name"
                                    placeholder="Item Name"
                                    {...register('item_name')}
                                    style={{ fontSize: '14px', height: '35px' }}
                                />
                                {errors?.item_name && <div className="invalid-feedback">{errors.item_name.message}</div>}
                            </div>
                        </div>

                        {/* Price */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="price" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Price: </label>
                            <div className="col-sm-8">
                                <Controller
                                    name="price"
                                    control={control}
                                    render={({ field }) => (
                                        <CurrencyInputRef
                                            name="price"
                                            className="app-form-control rounded-1"
                                            value={field.value}
                                            onChange={(_, value) => field.onChange(parseInt(value) || 0)}
                                            error={errors?.price?.message}
                                            placeholder="Rp. 0"
                                        />
                                    )}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="form-group mt-auto d-grid">
                        <button type="submit" className="btn btn-primary btn-block" disabled={isPending}>
                            {isPending ? 'Creating...' : 'Create'}
                        </button>
                    </div>
                </form>
            </Offcanvas.Body>
        </Offcanvas>
    )
}

export default CreateInventoryDialog
